// Pure helpers behind the Daily Tasks and Follow-Up receipts' sections (Chat.jsx).
// The task-meaning table at the bottom is shared with the task email template
// (lib/outreachDraft.js#taskFollowupDraft) so the card and the email agree.
//
// They live here, out of the page, for one reason: they are RULES, not
// rendering — how a raw HubSpot status reads and whether it is worth a badge
// at all, which held facts a customer card's evidence line may state — and
// rules need a node test
// (scripts/test-receiptSections.mjs) that does not have to stand up React to
// run. Nothing here fetches, and nothing here invents: every function writes
// only clauses backed by a value it was actually handed.

// ── HubSpot task statuses ────────────────────────────────────────────────────
//
// The status shown on each row is still the mirror's own raw string, never
// collapsed into our vocabulary. What is GONE (2026-09-02, at the owner's
// request) is the filter: the section used to carry a chip row — "All 42 /
// Not started 42" — computed from the statuses present. Once the window
// narrowed to overdue-or-due-today, essentially every row is NOT_STARTED, so
// the chips were two buttons selecting the same 42 rows. A control whose
// options cannot differ is not a filter, it is furniture.
//
// The per-row STATUS BADGE went the same way (2026-09-03, at the owner's
// request) for the same reason, and only that far: on this window NOT_STARTED
// is what a task looks like when nothing has happened to it, so a badge
// repeating it on all 42 rows is ink that distinguishes nothing. It is
// suppressed, not deleted — a row that really is In progress, Waiting or
// Deferred differs from the other 41 in the one way this section exists to
// show, and dropping the badge outright would hide exactly the row worth
// noticing. The raw value still rides on the row either way, so a row whose
// status stops being the resting one starts saying so again by itself.

/**
 * A raw status ("NOT_STARTED") as a readable label ("Not started").
 * Formatting only — underscores to spaces, sentence case — never a rename:
 * the raw value stays the filter key and rides in the chip's title.
 */
export function taskStatusLabel(raw) {
  const s = String(raw || '').trim()
  if (!s) return ''
  const lowered = s.replace(/_/g, ' ').toLowerCase()
  return lowered.charAt(0).toUpperCase() + lowered.slice(1)
}

// The status a task carries when nobody has touched it. Compared case- and
// underscore-insensitively rather than against the literal string, because the
// mirror's value is HubSpot's and this rule must not turn back on the day the
// portal starts sending 'not started'.
const TASK_STATUS_UNREMARKABLE = 'notstarted'

/**
 * The label to BADGE a task row with — '' when the status is not worth saying.
 *
 * The badge answers "is anything unusual about this row's status", not "what
 * is this row's status": the section is already filtered to overdue-or-due-
 * today, where NOT_STARTED is the resting state of every untouched task. So
 * the default returns '' and the row simply carries no badge, while every
 * other value HubSpot can send — In progress, Waiting, Deferred, Completed,
 * and anything the portal adds next month — still gets one without a deploy.
 */
export function taskStatusBadge(raw) {
  const label = taskStatusLabel(raw)
  if (!label) return ''
  return String(raw).trim().replace(/[\s_]/g, '').toLowerCase() === TASK_STATUS_UNREMARKABLE
    ? ''
    : label
}

// ── meeting times: GONE ─────────────────────────────────────────────────────
//
// chicagoTimeLabel and meetingTimeLabel lived here to render section 4's
// "Today's meetings" rows off Google Calendar. Section 4 was removed on
// 2026-09-03 at the owner's request — no calendar integration at all — so the
// only callers went with it and dead formatters are worse than absent ones:
// they read as a feature somebody forgot to wire up. Restore from git if the
// calendar lane ever comes back.

// ── the customer cards' evidence lines ──────────────────────────────────────
//
// One line under the name, made ONLY of held facts — the same discipline as
// every card in Chat.jsx: a field the item does not carry is a clause that is
// not written. Day counts come off the item (the engine computed them on
// Chicago days); when only the ISO date travelled, the date is shown instead
// of re-deriving a count in the browser's own zone.

// A short display date ("Aug 8") off an ISO string, or '' when unreadable.
export function shortDayLabel(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/Chicago',
  })
}

/**
 * The evidence line for one customer item.
 *   inactive:  "Last opened 12d ago · v4.2 · 7 projects"
 *   expired:   "Expired 30d ago (Aug 12) · last opened Sep 1"
 *   payment:   "Payment failed Sep 20 · 3 days ago · last opened Sep 21 · no card on file"
 *   trial:     "Trial ends Sep 12 · 9 days left · last opened Sep 1 · 3 projects · card on file"
 *              "Trial ends today · never opened the software · no card on file"
 * Tolerates both the engine's camelCase item fields and the snake_case the
 * draft API speaks, because the stub is packed by another writer.
 *
 * The trial line leads with WHEN, because the day count is what decides
 * whether Bo calls this morning or Thursday; then whether the product was ever
 * opened (a trial nobody opened is the first call, not a hidden one); then the
 * card, only when the portal actually holds a value — "no value" and "false"
 * are different facts, and only the second one gets a clause.
 */
export function customerEvidenceLine(item = {}, kind = 'inactive_user') {
  const days = (v) => (Number.isFinite(Number(v)) && Number(v) >= 0 ? Number(v) : null)
  const bits = []
  const lastAccess = item.lastSoftwareAccess ?? item.last_software_access
  if (kind === 'trial_convert') {
    const left = days(item.daysLeft ?? item.days_left)
    const endLabel = shortDayLabel(item.trialEnds ?? item.trial_ends)
    if (left === 0) bits.push('Trial ends today')
    else if (left === 1) bits.push(`Trial ends tomorrow${endLabel ? ` (${endLabel})` : ''}`)
    else if (left !== null) bits.push(`Trial ends ${endLabel || `in ${left} days`}${endLabel ? ` · ${left} days left` : ''}`)
    else if (endLabel) bits.push(`Trial ends ${endLabel}`)
    const accessLabel = shortDayLabel(lastAccess)
    if (item.everUsed === false) bits.push('never opened the software')
    else if (accessLabel) bits.push(`last opened ${accessLabel}`)
    const projects = Number(item.projectCount ?? item.project_count)
    if (Number.isFinite(projects) && projects > 0) {
      bits.push(`${projects} ${projects === 1 ? 'project' : 'projects'}`)
    }
    const card = item.paymentMethodAttached ?? item.payment_method_attached
    if (card === true) bits.push('card on file')
    else if (card === false) bits.push('no card on file')
  } else if (kind === 'expired_winback') {
    const dExpired = days(item.daysExpired ?? item.days_expired)
    // The end that actually lapsed: the engine's expiredAt (the later of the
    // trial and subscription ends), else whichever end the item holds.
    const endLabel = shortDayLabel(
      item.expiredAt ?? item.expired_at ?? item.trialEnds ?? item.trial_ends ?? item.subscriptionEnd ?? item.subscription_end,
    )
    if (dExpired !== null) bits.push(`Expired ${dExpired}d ago${endLabel ? ` (${endLabel})` : ''}`)
    else if (endLabel) bits.push(`Expired ${endLabel}`)
    const accessLabel = shortDayLabel(lastAccess)
    if (accessLabel) bits.push(`last opened ${accessLabel}`)
  } else if (kind === 'payment_failed') {
    // "Payment failed Sep 20 · 3 days ago · last opened Sep 21 · no card on file"
    const failedLabel = shortDayLabel(item.paymentFailedAt ?? item.payment_failed_at)
    const since = days(item.daysSinceFailure ?? item.days_since_failure)
    if (failedLabel) bits.push(`Payment failed ${failedLabel}${since !== null ? ` · ${since} ${since === 1 ? 'day' : 'days'} ago` : ''}`)
    else if (since !== null) bits.push(`Payment failed ${since} ${since === 1 ? 'day' : 'days'} ago`)
    const accessLabel = shortDayLabel(lastAccess)
    if (accessLabel) bits.push(`last opened ${accessLabel}`)
    const card = item.paymentMethodAttached ?? item.payment_method_attached
    if (card === true) bits.push('card on file')
    else if (card === false) bits.push('no card on file')
  } else {
    const dInactive = days(item.daysInactive ?? item.days_inactive)
    const accessLabel = shortDayLabel(lastAccess)
    if (dInactive !== null) bits.push(`Last opened ${dInactive}d ago`)
    else if (accessLabel) bits.push(`Last opened ${accessLabel}`)
    const version = String(item.softwareVersion ?? item.software_version ?? '').trim()
    if (version) bits.push(`v${version}`)
    const projects = Number(item.projectCount ?? item.project_count)
    if (Number.isFinite(projects)) {
      bits.push(`${projects} ${projects === 1 ? 'project' : 'projects'}`)
    }
  }
  return bits.join(' · ')
}

// ── the customer cards' status pills ────────────────────────────────────────
//
// Every Follow-Up card wears ONE pill naming the lane fact that put it there
// (owner's ask, 2026-09-23): a reader scanning thirty cards sees "Inactive
// 12d", "Trial ends in 3 days", "Trial expired 30d ago", "Payment failed"
// without reading the evidence line. Same discipline as the evidence line —
// only held facts; a day count the item does not carry is simply not said.
// `tone` is a hint the card maps to colour: 'danger' for what needs acting on
// today, 'warn' for soon, 'neutral' for lapsed.

/**
 * @returns {{label: string, tone: 'danger'|'warn'|'neutral'}|null}
 *   null only for an unknown kind — every lane kind gets a pill.
 */
export function customerStatusPill(item = {}, kind = 'inactive_user') {
  const days = (v) => (Number.isFinite(Number(v)) && Number(v) >= 0 ? Number(v) : null)
  if (kind === 'trial_convert') {
    const left = days(item.daysLeft ?? item.days_left)
    if (left === 0) return { label: 'Trial ends today', tone: 'danger' }
    if (left === 1) return { label: 'Trial ends tomorrow', tone: 'danger' }
    if (left !== null) return { label: `Trial ends in ${left} days`, tone: left <= 3 ? 'danger' : 'warn' }
    return { label: 'On trial', tone: 'warn' }
  }
  if (kind === 'expired_winback') {
    // A trial that lapsed and a subscription that lapsed read differently on
    // the pill; the item says which end date it actually holds. Stored
    // receipts from before the trial end rode on expired items carry only the
    // subscription end and keep the older wording.
    const what = item.trialEnds ?? item.trial_ends ? 'Trial expired' : 'Subscription expired'
    const d = days(item.daysExpired ?? item.days_expired)
    return { label: d !== null ? `${what} ${d}d ago` : what, tone: 'neutral' }
  }
  if (kind === 'payment_failed') {
    const d = days(item.daysSinceFailure ?? item.days_since_failure)
    return { label: d !== null ? `Payment failed ${d}d ago` : 'Payment failed', tone: 'danger' }
  }
  if (kind === 'inactive_user') {
    const d = days(item.daysInactive ?? item.days_inactive)
    return { label: d !== null ? `Inactive ${d}d` : 'Inactive', tone: 'warn' }
  }
  return null
}


// ── the task behind an email card ───────────────────────────────────────────
//
// A HubSpot task subject is the rep's own shorthand ("fu demo", "fu move to
// paid - See notes!"). The card and the template email both need it in plain
// words, and the one place that reads the shorthand is here, so the two can
// never disagree about what "fu onboard" means. Unknown wording is returned
// as itself — never guessed into a meaning the rep did not type.

const TASK_MEANINGS = [
  [/\b(demo)\b/i, 'getting a demo on the calendar'],
  [/\b(paid|convert|upgrade|subscri)/i, 'moving the account over to a paid plan'],
  [/\b(onboard|on-board|trial)\b/i, 'getting them set up and using Revailo'],
  [/\b(feedback|look|test|try)\b/i, 'hearing how the latest version is working for them'],
  [/\b(collab|partner)\b/i, 'the partnership conversation'],
  [/\b(pric|quote)/i, 'the pricing conversation'],
  [/\b(close out|close-out|closeout)\b/i, 'wrapping this up one way or the other'],
]

/**
 * The plain-words meaning of a task subject, or '' when the wording is not one
 * the rep uses as shorthand (the subject is then quoted as written instead).
 */
export function taskMeaning(subject) {
  const text = String(subject || '').trim()
  if (!text) return ''
  for (const [pattern, meaning] of TASK_MEANINGS) {
    if (pattern.test(text)) return meaning
  }
  return ''
}

/**
 * The evidence line under an email card's name: the task itself, and nothing
 * more. The due date, the days overdue and the deal used to follow it; they
 * were taken off the card on request (2026-10-11) - the status pill beside the
 * name already says overdue or due today.
 *   "Task: fu demo"
 */
export function taskEvidenceLine(item = {}) {
  const subject = String(item.subject || '').trim()
  return subject ? `Task: ${subject}` : ''
}

/**
 * The status pill an email card wears for its task: red when overdue, amber
 * when due today. null when the item says neither (an older row).
 */
export function taskStatusPill(item = {}) {
  const overdue = Number(item.overdueDays)
  if (item.overdue) {
    return {
      label: Number.isFinite(overdue) && overdue > 0 ? `Overdue ${overdue}d` : 'Overdue',
      tone: 'danger',
    }
  }
  if (item.dueToday) return { label: 'Due today', tone: 'warn' }
  return null
}
